// Traffic pool, lane paths and driving.
import * as THREE from 'three';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { Ph } from '../lib/factory.js';
import { streets } from '../world/landmarks.js';
import { treeTime } from '../world/trees.js';
import { laneGeometry, lanePose, pointInFootprint } from '../rules/lane-path.js';
import { lightPhase } from '../rules/traffic-light.js';
import { BIKE_SCALE, bikeParts, bodyMat } from './scooter.js';
import { S, wrapAngle } from '../game/ride.js';
import { CAR, cars, makeCar } from './cars.js';

export let TRAFFIC, TRAFFIC_MAX, npcBikes, driveCar;

export function buildTraffic() {
  // True when the nose of `c`, a moment from now along its lane, falls inside the footprint of `o`.
  const noseHits = (c, o) => {
    for (const d of [c.len / 2 + 0.4, c.len / 2 + 1.4, c.len / 2 + 2.6 + c.vv * 0.5]) {
      const p = lanePose(c.segs, (c.s + d) % c.total);
      if (
        pointInFootprint(p.x, p.z, o.g.position.x, o.g.position.z, o.h, o.len, o.w, c.w / 2 + 0.3)
      )
        return true;
    }
    return false;
  };

  driveCar = function driveCar(c, dt) {
    const fx = Math.sin(c.h),
      fz = Math.cos(c.h),
      px = c.g.position.x,
      pz = c.g.position.z;
    const rel = (x, z) => {
      const dx = x - px,
        dz = z - pz;
      return [dx * fx + dz * fz, Math.abs(dx * fz - dz * fx)];
    };
    let stop = false,
      hard = false; // hard stops (scooter in front, queue, red light) never time out
    {
      const [ahead, side] = rel(S.x, S.z);
      if (ahead > 0 && ahead < 2.4 + c.len / 2 && side < 1.2) stop = hard = true;
    }
    // red or amber light at the next junction on this leg: stop at the line (8.4 from the junction centre)
    if (!stop) {
      const k = c.leg ?? 0,
        [ax, az] = c.pts[k],
        [bx, bz] = c.pts[(k + 1) % 4],
        L = c.lens[k],
        dx = (bx - ax) / L,
        dz = (bz - az) / L;
      for (const jv of streets) {
        const jx = dx ? jv : ax,
          jz = dz ? jv : az,
          d = (jx - px) * dx + (jz - pz) * dz,
          line = 8.4 + c.len / 2;
        if (d < line - 0.3 || d > line + 2.2) continue;
        if (!streets.includes(jx - dx * 30) || !streets.includes(jz - dz * 30)) continue; // no signal on this approach
        if (lightPhase((jx + jz) / 30, dz !== 0, treeTime.value) < 2) stop = hard = true;
        break;
      }
    }
    if (!stop)
      for (const o of cars) {
        if (o === c || o.off) continue;
        const [ahead, side] = rel(o.g.position.x, o.g.position.z);
        const same = fx * Math.sin(o.h) + fz * Math.cos(o.h);
        if (c.lane !== o.lane && same > -0.7 && c.segs && o.segs) {
          // Where each nose will be a moment from now, following its own lane through the corners. If mine will end up
          // inside the other vehicle, I give way, unless the other is also about to hit me: then the lower index goes first.
          const mine = noseHits(c, o),
            theirs = mine && noseHits(o, c);
          if (mine && (!theirs || c.idx > o.idx) && (c.jam ?? 0) < 5) {
            stop = hard = true;
            break;
          }
        }
        if (ahead <= 0) continue;
        // cars on the same lane keep their gap along the lane itself, which stays right through the corners
        if (c.lane === o.lane && c.segs && o.segs) {
          const d = (((o.s - c.s) % c.total) + c.total) % c.total;
          if (d - (c.len + o.len) / 2 < 1.3 + c.vv * 0.4) {
            stop = hard = true;
            break;
          }
          continue;
        }
        if (same > 0.3) {
          // o's footprint seen from c's heading: how far it reaches sideways and back towards c
          const turn = Math.sqrt(1 - same * same),
            reachSide = (o.w / 2) * same + (o.len / 2) * turn,
            reachBack = (o.len / 2) * same + (o.w / 2) * turn;
          if (ahead - reachBack < 1.3 + c.len / 2 && side - reachSide < c.w / 2 + 0.2) {
            // roughly parallel: whoever is behind brakes hard; on a merge the lower index goes first
            if (same > 0.7 || o.idx < c.idx) stop = hard = same > 0.7;
            if (stop) break;
          }
        } else if (
          same > -0.3 &&
          ahead < 2 + c.len / 2 + o.w &&
          side < 1.6 + o.len / 2 &&
          o.idx < c.idx &&
          (o.vv ?? o.v) > 0.3
        ) {
          stop = true;
          break;
        }
      }
    // crossing traffic yields by priority; a long wait there forces the car through so nothing deadlocks
    c.jam = stop && hard && c.vv < 0.3 ? (c.jam ?? 0) + dt : 0;
    c.wait = stop && !hard ? (c.wait || 0) + dt : 0;
    if (c.wait > 2.5) stop = false;
    c.vv = THREE.MathUtils.lerp(c.vv ?? c.v, stop ? 0 : c.v, Math.min(1, (stop ? 6 : 1.5) * dt));
    if (!c.segs) {
      Object.assign(c, laneGeometry(c.pts));
      c.s %= c.total;
    }
    c.s = (c.s + c.vv * dt) % c.total;
    const { x, z, tx, tz, leg } = lanePose(c.segs, c.s);
    c.leg = leg;
    c.h += wrapAngle(Math.atan2(tx, tz) - c.h) * Math.min(1, 12 * dt);
    c.g.position.set(x, 0, z);
    c.g.rotation.y = c.h;
    if (c.wheels) for (const w of c.wheels) w.rotation.x += (c.vv * dt) / 0.2;
  };
  // traffic density: a pool of vehicles is built once; the chosen level decides how many are on the road
  TRAFFIC = { low: 9, mid: 18, high: 32 };
  TRAFFIC_MAX = TRAFFIC.high;
  const LOOPS = [
    [
      [-45, -45],
      [45, -45],
      [45, 45],
      [-45, 45],
    ],
    [
      [-15, -15],
      [15, -15],
      [15, 15],
      [-15, 15],
    ],
    [
      [-45, -15],
      [15, -15],
      [15, 45],
      [-45, 45],
    ],
    [
      [-15, -45],
      [45, -45],
      [45, 15],
      [-15, 15],
    ],
  ];
  const carColors = [0xd94b3d, 0x2f6fd0, 0xf2f0ea, 0x3a3f47, 0x58a36b, 0xe8b33b, 0x8fb8d9];
  // lanes are (loop, direction) pairs; vehicles are dealt round-robin and spaced evenly along their lane
  const laneCount = {},
    laneIdx = [];
  for (let i = 0; i < TRAFFIC_MAX; i++) {
    const key = (i % LOOPS.length) * 2 + (i % 2);
    laneIdx.push((laneCount[key] = (laneCount[key] ?? -1) + 1));
  }
  for (let i = 0; i < TRAFFIC_MAX; i++) {
    const taxi = i >= 3 && rnd() < 0.32,
      type =
        i < 2 || i === 20
          ? 'bus'
          : taxi
            ? pick(['hatch', 'sedan'])
            : pick(['hatch', 'hatch', 'sedan', 'van']);
    const color =
      type === 'bus'
        ? 0xd2302a
        : taxi
          ? 0x1b1b1d
          : type === 'van'
            ? pick([0xf2f0ea, 0xf2f0ea, 0x2f6fd0])
            : pick(carColors);
    const g = makeCar(type, color, taxi),
      loop = LOOPS[i % LOOPS.length],
      dir = i % 2 ? 1 : -1;
    const pts = dir > 0 ? loop : [...loop].reverse(),
      lens = pts.map((p, k) => Math.hypot(pts[(k + 1) % 4][0] - p[0], pts[(k + 1) % 4][1] - p[1]));
    const total = lens.reduce((a, b) => a + b, 0);
    const key = (i % LOOPS.length) * 2 + (i % 2),
      slot = laneIdx[i],
      n = laneCount[key] + 1;
    cars.push({
      g,
      pts,
      lens,
      total,
      s: ((slot / n) * total + 11 * key) % total,
      h: 0,
      v: type === 'bus' ? 4.5 : rr(5.5, 8),
      len: CAR[type].len,
      w: CAR[type].w,
      pool: i,
      lane: key,
      type,
      color,
      taxi,
    });
    scene.add(g);
  }

  // a few other scooter riders share the road, on the same bike in other colours (riders added when the models load)
  npcBikes = [];
  for (let i = 0; i < 5; i++) {
    const g = new THREE.Group(),
      body = Ph([0xc8392e, 0xf2f0ea, 0x1d1f22, 0x2f6fd0, 0xb8bcc2][i], {
        roughness: 0.28,
        metalness: 0.6,
      }),
      bike = new THREE.Group();
    bike.scale.setScalar(BIKE_SCALE);
    g.add(bike);
    for (const part of bikeParts) {
      const m = new THREE.Mesh(part.geo, part.mat === bodyMat ? body : part.mat);
      m.castShadow = m.receiveShadow = true;
      bike.add(m);
    }
    npcBikes.push(bike);
    const loop = LOOPS[i % LOOPS.length],
      dir = i % 2 ? -1 : 1,
      pts = dir > 0 ? loop : [...loop].reverse();
    const lens = pts.map((p, k) =>
        Math.hypot(pts[(k + 1) % 4][0] - p[0], pts[(k + 1) % 4][1] - p[1]),
      ),
      total = lens.reduce((a, b) => a + b, 0);
    scene.add(g);
    cars.push({
      g,
      pts,
      lens,
      total,
      s: (i * 53.7 + 20) % total,
      h: 0,
      v: rr(6, 9),
      len: 1.2,
      w: 0.55,
      lane: (i % LOOPS.length) * 2 + (i % 2 ? 0 : 1),
    });
  }
  // position along the loop, right-hand lane offset, smooth heading; waits for the scooter and for the car ahead
  // Following only applies to traffic in the same direction. Crossing traffic yields by a fixed priority
  // (lower index goes first), so two cars can never wait for each other. A long wait forces the car through.
}
